// A deploy renames every chunk. This is what keeps an open tab from breaking.
//
// ── THE FAILURE ──────────────────────────────────────────────────────────────
//
// Vite content-hashes each lazy chunk, so `PurchasesPage-BpxxsQ-8.js` becomes
// `PurchasesPage-<new hash>.js` on the next deploy and the old name stops
// existing. A tab that was open across that deploy is still running the OLD
// index bundle, which knows only the old names — so the first route the user
// clicks fetches a URL that 404s and React Router hands them:
//
//     Failed to fetch dynamically imported module:
//     https://quarterstack.app/assets/PurchasesPage-BpxxsQ-8.js
//
// A refresh fixes it, because that pulls the new index. So the fix is to do
// the refresh for them, at the only moment we know it is needed.
//
// ── WHY A RELOAD IS THE RIGHT ANSWER, NOT A RETRY ────────────────────────────
//
// Re-fetching the same URL cannot help: the file is gone, and a cache-busting
// query string would only turn a 404 into a different 404. The running app is
// what is stale, so the running app is what has to be replaced. The user had
// already asked to leave this page, so a reload costs them nothing — and the
// editor pages autosave drafts (M74k `draftStorage`), so even a half-typed form
// survives.
//
// ── THE LOOP GUARD IS THE LOAD-BEARING PART ──────────────────────────────────
//
// "On failure, reload" is one typo away from an infinite refresh loop that
// nobody can escape — the worst possible outcome, far worse than the error
// screen it replaces. A chunk can fail for reasons a reload will never fix: an
// ad blocker, a captive portal, a genuinely broken deploy. So each chunk gets
// ONE reload per session, recorded in sessionStorage before the reload happens.
// The second failure falls through to the route's `errorElement`, which is an
// honest screen with a manual Reload on it.
//
// sessionStorage, not memory: the reload wipes memory, which is exactly when we
// need to remember. Per tab, and cleared when the tab closes — a user who comes
// back tomorrow gets a fresh attempt, which is correct, because by then the
// cause is probably different.

import { lazy, type ComponentType } from 'react';

const KEY_PREFIX = 'qs.chunkReload.';

/** Vite/browser wording for "the module fetch itself failed". Chrome, Firefox
 *  and Safari each phrase it differently, hence the alternation. A render error
 *  INSIDE the module is a different animal and must not trigger a reload. */
const CHUNK_LOAD_FAILURE = /(dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch)/i;

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return CHUNK_LOAD_FAILURE.test(message);
}

/** Test seam — the guard lives in sessionStorage, which persists across a test. */
export function _clearChunkReloadGuards(): void {
  try {
    for (const k of Object.keys(sessionStorage)) {
      if (k.startsWith(KEY_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch { /* storage unavailable; nothing to clear */ }
}

/** Has this chunk already had its one reload this session? */
function alreadyRetried(key: string): boolean {
  try {
    return sessionStorage.getItem(KEY_PREFIX + key) !== null;
  } catch {
    // Private mode, or storage blocked. Without a guard we cannot promise the
    // reload terminates, so we decline to reload at all and let the error
    // surface. A visible error beats a loop the user cannot break out of.
    return true;
  }
}

function markRetried(key: string): void {
  try {
    sessionStorage.setItem(KEY_PREFIX + key, String(Date.now()));
  } catch { /* see above — we never get here, alreadyRetried() short-circuits */ }
}

/**
 * `React.lazy`, plus recovery from the chunk a deploy deleted.
 *
 * @param key   A stable name for this route's chunk. It is the guard's
 *              identity, so it must not change between the failure and the
 *              reload — hence a hand-written string rather than anything
 *              derived from the hashed filename, which is the thing that moved.
 */
export function lazyRoute<T extends ComponentType<unknown>>(
  key: string,
  importer: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      if (!isChunkLoadError(err) || alreadyRetried(key)) throw err;
      markRetried(key);
      window.location.reload();
      // The reload is asynchronous: without this the promise would reject and
      // React would paint the error screen for the instant before the document
      // is torn down. A promise that never settles keeps the Suspense fallback
      // up until the new document takes over.
      return new Promise<never>(() => {});
    }
  });
}
